import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { getItemTotal } from "@/utils/cart-utils";
import { roundPrice } from "@/utils/app-helper";
import type { MenuItemInterface } from "@/models/menu-item";
import type { DietaryType } from "@/utils/tag-dietary";
import { getDietaryType } from "@/utils/tag-dietary";
import { useCartStore } from "@/store/cart/cart-store";
import { selectCartItems } from "@/store/cart/cart.selectors";
import type { CartLine } from "@/store/cart/cart.types";
import { cartLineToCartItemInterface } from "@/store/cart/cart-line-adapters";
import type { CartItemInterface } from "@/models/cart-item";
import type { Discount } from "@/models/discount";

export interface CartItemDisplayAddon {
  addon_id: string;
  addon_name?: string;
  addon_price: number;
  addon_quantity: number;
  addon_line_total: number;
  /** Catalog group label when available (derived from menu snapshot). */
  modifier_group_label?: string;
}

export interface CartItemDisplay {
  client_item_id?: string;
  menu_item_id: string;
  item_name: string;
  quantity: number;
  item_price: number;
  dietaryType?: DietaryType | null;
  addons?: CartItemDisplayAddon[];
  total_price: number;
  /** Reorder best-effort: customer should review customizations. */
  needsReview?: boolean;
  removedCustomizationNames?: string[];
}

function safePrice(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Checkout/order-review label: strips instructional catalog copy (e.g. "CHOOSE 2")
 * and normalizes to concise title-style text. Presentation-only.
 */
export function formatModifierGroupSummaryLabel(raw: string): string {
  let s = raw.trim().replace(/\s+/g, " ");
  if (!s) return "";
  s = s.replace(/^(choose|select|pick)\s+\d+\s*[-–]\s*\d+\s+/i, "");
  s = s.replace(/^(choose|select|pick)\s+\d+\s+/i, "");
  s = s.replace(/^(choose|select|pick)\s+/i, "");
  const letters = s.replace(/[^a-zA-ZÀ-ÿ]/g, "");
  if (letters.length > 0 && s === s.toUpperCase()) {
    s = s.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());
  }
  return s.trim();
}

/** Key: `${menu_item_id}::${modifier_group_id}` → human-readable group title */
function buildModifierGroupLabelLookup(menuItems: MenuItemInterface[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const m of menuItems) {
    const mid = String(m.menu_item_id ?? "");
    if (!mid) continue;
    for (const g of m.modifier_groups ?? []) {
      const gid = String(g?.modifier_group_id ?? "").trim();
      if (!gid) continue;
      const raw = String(g?.display_name ?? g?.group_name ?? "").trim();
      const label = formatModifierGroupSummaryLabel(raw);
      if (label) map.set(`${mid}::${gid}`, label);
    }
  }
  return map;
}

function buildModifierNameLookup(menuItems: MenuItemInterface[]): Map<string, string> {
  const byMenuItemId = new Map<string, string>();
  for (const m of menuItems) {
    byMenuItemId.set(String(m.menu_item_id), String(m.item_name ?? "").trim());
  }
  const byModifierItemId = new Map<string, string>();
  for (const m of menuItems) {
    const groups = m.modifier_groups ?? [];
    for (const g of groups) {
      for (const it of g.items ?? []) {
        const modifierItemId = String(it.modifier_group_item_id);
        if (!modifierItemId || byModifierItemId.has(modifierItemId)) continue;
        const childId = String(it.child_menu_item_id);
        const childName = byMenuItemId.get(childId);
        if (childName && childName.trim() !== "") {
          byModifierItemId.set(modifierItemId, childName);
        }
      }
    }
  }
  return byModifierItemId;
}

function getModifierDisplayName(itemId: string, lookup: Map<string, string>): string {
  const name = lookup.get(itemId);
  if (name && name.trim() !== "") return name;
  return "Unknown Item";
}

export function mapItemToDisplay(
  item: CartItemInterface,
  modifierNameLookup: Map<string, string>,
  dietaryType: DietaryType | null,
  modifierGroupLabelLookup: Map<string, string>
): CartItemDisplay {
  const count = Math.max(0, Math.floor(Number(item?.count) ?? 0));
  const rawMods = item?.selectedModifiers ?? [];
  const grouped = new Map<string, { itemId: string; price: number; quantity: number; groupId: string }>();
  for (const m of rawMods) {
    const itemId = String(m.itemId);
    const price = safePrice(m?.delta_price);
    const mq = Math.max(1, Math.floor(Number(m?.quantity) || 1));
    const key = `${itemId}|${price}`;
    const prev = grouped.get(key);
    const gid = String(m.groupId ?? "").trim();
    grouped.set(key, {
      itemId,
      price,
      quantity: (prev?.quantity ?? 0) + mq,
      groupId: prev?.groupId || gid,
    });
  }
  const menuItemId = String(item?.menu_item_id ?? "");
  const addons: CartItemDisplayAddon[] = Array.from(grouped.values()).map((g) => {
    const gkey = g.groupId ? `${menuItemId}::${g.groupId}` : "";
    const modifier_group_label =
      gkey && modifierGroupLabelLookup.has(gkey) ? modifierGroupLabelLookup.get(gkey)! : undefined;
    return {
      addon_id: g.itemId,
      addon_name: getModifierDisplayName(g.itemId, modifierNameLookup),
      addon_price: g.price,
      addon_quantity: count * g.quantity,
      addon_line_total: roundPrice(g.price * count * g.quantity),
      modifier_group_label,
    };
  });
  return {
    menu_item_id: String(item?.menu_item_id ?? ""),
    item_name: typeof item?.item_name === "string" ? item.item_name : "",
    quantity: count,
    item_price: safePrice(item?.item_price),
    dietaryType,
    addons,
    total_price: Math.max(0, getItemTotal(item)),
  };
}

export function mapCartLineToDisplay(
  line: CartLine,
  modifierNameLookup: Map<string, string>,
  dietaryType: DietaryType | null,
  modifierGroupLabelLookup: Map<string, string>
): CartItemDisplay {
  const base = mapItemToDisplay(
    cartLineToCartItemInterface(line),
    modifierNameLookup,
    dietaryType,
    modifierGroupLabelLookup
  );
  return {
    ...base,
    client_item_id: line.client_item_id,
    needsReview: line.needsReview === true ? true : undefined,
    removedCustomizationNames:
      line.removedCustomizationNames && line.removedCustomizationNames.length > 0
        ? [...line.removedCustomizationNames]
        : undefined,
  };
}

export function useCartItemsDisplay(): CartItemDisplay[] {
  const items = useCartStore(selectCartItems);
  const menuItems = useSelector((state: RootState) => state.menuItems?.list ?? []);
  return useMemo(() => {
    if (!Array.isArray(items)) return [];
    const safeMenuItems = Array.isArray(menuItems) ? menuItems : [];
    const modifierNameLookup = buildModifierNameLookup(safeMenuItems);
    const modifierGroupLabelLookup = buildModifierGroupLabelLookup(safeMenuItems);
    const dietaryByMenuItemId = new Map<string, DietaryType | null>();
    for (const mi of safeMenuItems) {
      dietaryByMenuItemId.set(String(mi.menu_item_id), getDietaryType(mi.tags));
    }
    return items
      .filter((i) => (i?.quantity ?? 0) >= 1)
      .map((i) =>
        mapCartLineToDisplay(
          i,
          modifierNameLookup,
          dietaryByMenuItemId.get(String(i?.menu_item_id ?? "")) ?? null,
          modifierGroupLabelLookup
        )
      );
  }, [items, menuItems]);
}

/** Next spend tier from available offers (AOV hint). */
export type CartNextSavingsHint = {
  requiredAmount: number;
  potentialSavings: number;
};

const MONEY_EQ = 0.005;

/** Hide “next tier” hint when the gap is more than this × current subtotal (keeps UX trustworthy). */
export const NEXT_TIER_MAX_GAP_RATIO = 0.4;

/** Do not show next-tier hints when subtotal is below this (avoids noisy hints on tiny carts). */
export const NEXT_TIER_HINT_MIN_SUBTOTAL = 200;

/**
 * Normalizes `nextSavings` for UI: null when absent or non-positive legs.
 * Use with `useMemo(..., [snapshot.nextSavings])` so callers do not rebuild objects in JSX.
 */
export const selectStableNextSavings = (nextSavings?: CartNextSavingsHint | null): CartNextSavingsHint | null => {
  if (!nextSavings) return null;
  const { requiredAmount, potentialSavings } = nextSavings;
  if (requiredAmount <= 0 || potentialSavings <= 0) return null;
  return { requiredAmount, potentialSavings };
};

function readMinOrderAmount(d: Discount): number {
  const raw =
    (d as { min_order_amount?: unknown }).min_order_amount ?? (d as { minOrderAmount?: unknown }).minOrderAmount;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? roundPrice(n) : 0;
}

function isDiscountDateWindowOpen(d: Discount): boolean {
  try {
    const now = Date.now();
    const start = new Date(d.start_datetime).getTime();
    const end = new Date(d.end_datetime).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return true;
    return now >= start && now <= end;
  } catch {
    return true;
  }
}

/**
 * Estimate savings from `discount` if order subtotal reached `hypotheticalOrderSubtotal`
 * (linear scale of category-scoped eligible lines vs current subtotal).
 */
export function computeDiscountAtHypotheticalOrderSubtotal(
  items: CartLine[],
  discount: Discount,
  hypotheticalOrderSubtotal: number,
  currentOrderSubtotal: number
): number {
  if (!discount?.discount_id) return 0;

  const hypo = roundPrice(hypotheticalOrderSubtotal);
  const cur = roundPrice(currentOrderSubtotal);
  const scale = cur > MONEY_EQ ? hypo / cur : 1;

  const lineEligible = (item: CartLine) =>
    discount.item_category?.toLowerCase() === "all" || item.category === discount.item_category;

  const currentEligibleSum = items.reduce((sum, item) => {
    if (!lineEligible(item)) return sum;
    const line = getItemTotal(item);
    return sum + (isNaN(line) ? 0 : line);
  }, 0);

  const discountedItemsAmount =
    discount.item_category?.toLowerCase() === "all" ? hypo : roundPrice(currentEligibleSum * scale);

  const discountType = discount.discount_type?.toUpperCase();

  if (discountType === "FLAT" && discount.discount_value != null) {
    const v = Number(discount.discount_value);
    if (!isNaN(v) && v > 0) return roundPrice(Math.min(v, discountedItemsAmount));
  }

  if (discountType === "PERCENTAGE" || !discountType) {
    const percentage = Number(discount.percentage) || 0;
    if (!isNaN(percentage) && percentage > 0 && !isNaN(discountedItemsAmount)) {
      return roundPrice((discountedItemsAmount * percentage) / 100);
    }
  }

  if (discount.discount_value != null) {
    const v = Number(discount.discount_value);
    if (!isNaN(v) && v > 0) return roundPrice(Math.min(v, discountedItemsAmount));
  }

  return 0;
}

/**
 * Next minimum-cart tier the shopper has not yet reached (smallest `min_order_amount` above current subtotal).
 * Suppressed when {@link NEXT_TIER_HINT_MIN_SUBTOTAL} is not met, or when spend-to-next exceeds
 * {@link NEXT_TIER_MAX_GAP_RATIO} × current subtotal (see `selectStableNextSavings` for UI shaping).
 */
export function computeNextCartSavingsHint(
  items: CartLine[],
  currentSubtotalRounded: number,
  selectedDiscount: Discount | undefined,
  list: Discount[]
): CartNextSavingsHint | null {
  if (!items.length || currentSubtotalRounded <= 0) return null;
  if (currentSubtotalRounded + MONEY_EQ < NEXT_TIER_HINT_MIN_SUBTOTAL) return null;

  const merged: Discount[] = [...list];
  if (
    selectedDiscount?.discount_id &&
    !merged.some((x) => String(x.discount_id) === String(selectedDiscount.discount_id))
  ) {
    merged.push(selectedDiscount);
  }

  let best: { minM: number; gap: number; savings: number } | null = null;

  for (const d of merged) {
    if (!d?.discount_id) continue;
    if (d.is_active === false) continue;
    if (!isDiscountDateWindowOpen(d)) continue;

    const minM = readMinOrderAmount(d);
    if (minM <= 0) continue;
    if (currentSubtotalRounded + MONEY_EQ >= minM) continue;

    const savings = computeDiscountAtHypotheticalOrderSubtotal(items, d, minM, currentSubtotalRounded);
    if (!(savings > MONEY_EQ)) continue;

    const gap = roundPrice(minM - currentSubtotalRounded);
    if (!(gap > MONEY_EQ)) continue;

    if (
      !best ||
      minM < best.minM - MONEY_EQ ||
      (Math.abs(minM - best.minM) <= MONEY_EQ && savings > best.savings + MONEY_EQ)
    ) {
      best = { minM, gap, savings: roundPrice(savings) };
    }
  }

  if (!best) return null;
  if (best.gap > currentSubtotalRounded * NEXT_TIER_MAX_GAP_RATIO + MONEY_EQ) {
    return null;
  }
  return { requiredAmount: best.gap, potentialSavings: best.savings };
}

/**
 * Client-side discount estimate for the selected coupon (mirrors checkout cart rules:
 * category scope, FLAT vs PERCENTAGE, then fallback `discount_value`).
 */
export function computeSelectedCartDiscount(
  items: CartLine[],
  selectedDiscount: Discount | undefined,
  subtotal: number
): number {
  if (!selectedDiscount?.discount_id) return 0;

  const discountedItemsAmount =
    selectedDiscount.item_category?.toLowerCase() === "all"
      ? subtotal
      : items
          .filter((item) => item.category === selectedDiscount.item_category)
          .reduce((sum, item) => {
            const line = getItemTotal(item);
            return sum + (isNaN(line) ? 0 : line);
          }, 0);

  const discountType = selectedDiscount.discount_type?.toUpperCase();

  if (discountType === "FLAT" && selectedDiscount.discount_value != null) {
    const v = Number(selectedDiscount.discount_value);
    return !isNaN(v) && v > 0 ? v : 0;
  }

  if (discountType === "PERCENTAGE" || !discountType) {
    const percentage = Number(selectedDiscount.percentage) || 0;
    if (!isNaN(percentage) && percentage > 0 && !isNaN(discountedItemsAmount)) {
      return (discountedItemsAmount * percentage) / 100;
    }
  }

  if (selectedDiscount.discount_value != null) {
    const v = Number(selectedDiscount.discount_value);
    return !isNaN(v) && v > 0 ? v : 0;
  }

  return 0;
}

export type CartPricingSnapshot = {
  subtotal: number;
  discount: number;
  total: number;
  nextSavings: CartNextSavingsHint | null;
};

/**
 * Cart subtotal, estimated coupon discount, payable total, and optional next-tier hint.
 * `availableDiscounts` drives next-tier messaging (typically `state.discounts.list`).
 */
export function getCartPricingSnapshot(
  items: CartLine[],
  selectedDiscount: Discount | undefined,
  availableDiscounts: Discount[] = []
): CartPricingSnapshot {
  if (!items.length) {
    return { subtotal: 0, discount: 0, total: 0, nextSavings: null };
  }

  const subtotal = items.reduce((sum, item) => {
    const total = getItemTotal(item);
    return sum + (isNaN(total) ? 0 : total);
  }, 0);

  const discountRaw = computeSelectedCartDiscount(items, selectedDiscount, subtotal);
  const discount = isNaN(discountRaw) ? 0 : discountRaw;

  const totalRaw = subtotal - discount;
  const total = isNaN(totalRaw) ? 0 : Math.max(0, totalRaw);

  const subR = roundPrice(subtotal);
  const discR = roundPrice(discount);
  const totR = roundPrice(total);

  return {
    subtotal: subR,
    discount: discR,
    total: totR,
    nextSavings: computeNextCartSavingsHint(items, subR, selectedDiscount, availableDiscounts),
  };
}

export function getCartEstimatedTotal(items: CartLine[], selectedDiscount: Discount | undefined): number {
  return getCartPricingSnapshot(items, selectedDiscount).total;
}

export function useCartEstimatedTotal(): number {
  const items = useCartStore(selectCartItems);
  const selected = useSelector((s: RootState) => s.discounts?.selected);
  return useMemo(() => getCartPricingSnapshot(items, selected).total, [items, selected]);
}

export function useCartPricingSnapshot(): CartPricingSnapshot {
  const items = useCartStore(selectCartItems);
  const selected = useSelector((s: RootState) => s.discounts?.selected);
  const list = useSelector((s: RootState) => s.discounts?.list ?? []);
  return useMemo(() => getCartPricingSnapshot(items, selected, list), [items, selected, list]);
}

/** Sum of line quantities (badge / quick info). */
export function useCartItemCount(): number {
  return useCartStore((s) =>
    selectCartItems(s).reduce((sum, item) => sum + (item?.quantity ?? 0), 0)
  );
}
